import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ID } from '@gh/common-shared-data';
import { LeadRestService, OfficeRestService } from '@gh/builder-shared-data';
import { AutocompleteLoadMoreEvent, NotificationService, STYLES_DIALOG_LAYOUT, WaitingService } from '@gh/core-ui';
import { sortByStringField } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { finalize$, map$ } from '@gh/rx/operators';
import { Office } from '../../../builder-shared/switch-to/resources/office.model';
import { LeadListBusinessService, UserDetailsShort } from '../../resources/lead-list-business.service';

export type CopyArchiveLeadDialogData = {
  leadId: ID;
  sourceOfficeId?: ID;
  masterZoneId?: ID;
};

@Component({
  standalone: false,
  selector: 'gh-copy-archive-lead-dialog',
  templateUrl: 'copy-archive-lead-dialog.component.html',
  styleUrls: ['copy-archive-lead-dialog.component.scss'],
  styles: [STYLES_DIALOG_LAYOUT],
})
export class CopyArchiveLeadDialogComponent implements OnInit {
  offices: Office[] = [];
  salesPersons: UserDetailsShort[] = [];
  selectedOffice: Office | null = null;
  selectedOfficeId?: ID;
  selectedSalesPerson: UserDetailsShort | null = null;
  selectedSalesPersonId?: ID;
  /** True while active offices are loaded from the API. */
  officesLoading = false;
  /** True if offices have already been fetched at least once. */
  officesLoaded = false;
  /** True while salespersons are loaded for the selected office. */
  salesPersonsLoading = false;
  private pendingOfficeLoad?: AutocompleteLoadMoreEvent;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: CopyArchiveLeadDialogData,
    private dialogRef: MatDialogRef<CopyArchiveLeadDialogComponent>,
    private leadRest: LeadRestService,
    private officeRest: OfficeRestService,
    private leadListBusiness: LeadListBusinessService,
    private notificationService: NotificationService,
    private waiting: WaitingService,
  ) {}

  ngOnInit(): void {
    this.loadOffices();
  }

  loadOfficeOptions(event: AutocompleteLoadMoreEvent): void {
    if (this.officesLoaded) {
      this.emitOfficeOptions(event);
      return;
    }
    this.pendingOfficeLoad = event;
    if (!this.officesLoading) {
      this.loadOffices();
    }
  }

  onOfficeSelected(office: Office): void {
    const officeId = office?.id;
    if (officeId === this.selectedOfficeId) {
      return;
    }
    this.selectedOffice = office || null;
    this.selectedOfficeId = officeId;
    this.onOfficeChange();
  }

  onOfficeChange(): void {
    this.selectedSalesPerson = null;
    this.selectedSalesPersonId = undefined;
    this.salesPersons = [];

    if (!this.selectedOfficeId) {
      return;
    }

    this.salesPersonsLoading = true;
    this.leadListBusiness
      .findSalesPersonsByName({ from: 0, to: 1000 }, {
        officeIds: [this.selectedOfficeId],
        showInactive: false,
      })
      .pipe(
        this.waiting.operator(),
        finalize$(() => {
          this.salesPersonsLoading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          this.salesPersons = res.filter((sp) => sp.id !== '');
          if (this.salesPersons.length === 1) {
            this.selectedSalesPerson = this.salesPersons[0];
            this.selectedSalesPersonId = this.salesPersons[0].id;
          }
        },
        error: (err) => {
          this.notificationService.processAll(err);
        },
      });
  }

  loadSalesPersonOptions(event: AutocompleteLoadMoreEvent): void {
    const query = (event.query.filter || '').toLowerCase();
    event.load(of$(query
      ? this.salesPersons.filter((sp) => (sp.name || '').toLowerCase().includes(query))
      : this.salesPersons));
  }

  onSalesPersonSelected(salesPerson: UserDetailsShort): void {
    this.selectedSalesPerson = salesPerson || null;
    this.selectedSalesPersonId = salesPerson?.id;
  }

  private loadOffices(): void {
    const excludeSource = (list: Office[]) =>
      list.filter((o) => String(o.id) !== String(this.data.sourceOfficeId ?? ''));

    if (this.data.masterZoneId) {
      this.officesLoading = true;
      this.officeRest
        .findAllByMasterZone(this.data.masterZoneId, ['ACTIVE'], ['FRANCHISE'])
        .pipe(
          map$((list) => sortByStringField(excludeSource(list), 'name')),
          finalize$(() => {
            this.officesLoading = false;
            this.officesLoaded = true;
          }),
        )
        .subscribe({
          next: (list) => {
            this.offices = list;
            this.flushPendingOfficeLoad();
          },
          error: (err) => {
            this.notificationService.processAll(err);
            this.flushPendingOfficeLoad();
          },
        });
      return;
    }

    // Corporate leads: Master Zone “All” — same active franchise list as bulk reassign (POST /offices/finder).
    this.officesLoading = true;
    this.leadListBusiness
      .findAllActiveOfficesAcrossMasterZones()
      .pipe(
        map$((list) => sortByStringField(excludeSource(list), 'name')),
        finalize$(() => {
          this.officesLoading = false;
          this.officesLoaded = true;
        }),
      )
      .subscribe({
        next: (list) => {
          this.offices = list;
          this.flushPendingOfficeLoad();
        },
        error: (err) => {
          this.notificationService.processAll(err);
          this.flushPendingOfficeLoad();
        },
      });
  }

  private flushPendingOfficeLoad(): void {
    if (!this.pendingOfficeLoad) {
      return;
    }
    this.emitOfficeOptions(this.pendingOfficeLoad);
    this.pendingOfficeLoad = undefined;
  }

  private emitOfficeOptions(event: AutocompleteLoadMoreEvent): void {
    const query = (event.query.filter || '').toLowerCase();
    event.load(of$(query
      ? this.offices.filter((office) => (office.name || '').toLowerCase().includes(query))
      : this.offices));
  }

  isConfirmDisabled(): boolean {
    return this.officesLoading
      || this.salesPersonsLoading
      || !this.selectedOfficeId
      || !this.selectedSalesPersonId;
  }

  onConfirm(): void {
    if (!this.selectedOfficeId || !this.selectedSalesPersonId) {
      return;
    }

    this.leadRest
      .copyArchive({
        leadId: this.data.leadId,
        targetOfficeId: this.selectedOfficeId,
        targetSalespersonId: this.selectedSalesPersonId,
      })
      .pipe(this.waiting.operator())
      .subscribe(
        () => {
          this.notificationService.success('msg_clone_archive_lead_success');
          this.dialogRef.close(true);
        },
        (err) => {
          this.notificationService.processAll(err);
        },
      );
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
